import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeOption = 'light' | 'dark' | 'high-contrast-dark';

interface ThemeContextProps {
  themeMode: ThemeOption;
  setThemeMode: Dispatch<SetStateAction<ThemeOption>>;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [themeMode, setThemeMode] = useState<ThemeOption>(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'high-contrast-dark') {
      return storedTheme;
    }
    return 'light';
  });

  useEffect(() => {
    if (themeMode === 'light') {
      localStorage.setItem('theme', 'light');
      document.documentElement.dataset.colorScheme = 'light';
    } else if (themeMode === 'dark') {
      localStorage.setItem('theme', 'dark');
      document.documentElement.dataset.colorScheme = 'dark';
    } else if (themeMode === 'high-contrast-dark') {
      localStorage.setItem('theme', 'high-contrast-dark');
      document.documentElement.dataset.colorScheme = 'high-contrast-dark';
    }
  }, [themeMode]);

  const memoizedValue = useMemo(() => ({ themeMode, setThemeMode }), [themeMode, setThemeMode]);

  return <ThemeContext.Provider value={memoizedValue}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};
